import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/program', label: 'Program' },
    { path: '/speakers', label: 'Speakers' },
    { path: '/partners', label: 'Partners' },
    { path: '/beirut', label: 'Beirut' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-sm' 
          : 'bg-transparent'
      }`}
    >
      <div className="px-6 lg:px-12 xl:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Left aligned with version switching */}
          <Link 
            to="/" 
            className="flex items-center transition-transform duration-300 hover:scale-105"
          >
            {isScrolled ? (
              // Dark version for white background
              <div className="bg-[#0a1628] rounded-lg p-1">
                <img
                  src="https://static.readdy.ai/image/fae9e38b84e40674aa7550f041b63695/a464937ca27083cedc8a3bc7a19a7622.jpeg"
                  alt="WealthRise Management"
                  className="h-14 w-auto rounded-md"
                />
              </div>
            ) : (
              // White/transparent version for dark background
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                <div className="text-white">
                  <div className="font-display text-xl font-bold tracking-tight leading-none">
                    WealthRise
                  </div>
                  <div className="text-[10px] font-sans tracking-[0.15em] text-champagne-300 mt-0.5">
                    MANAGEMENT
                  </div>
                </div>
              </div>
            )}
          </Link>

          {/* Desktop Navigation - Right aligned */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 whitespace-nowrap group ${
                  location.pathname === link.path
                    ? isScrolled
                      ? 'text-champagne-600'
                      : 'text-white'
                    : isScrolled
                    ? 'text-navy-700 hover:text-champagne-600'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
                <span 
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-champagne-600 transition-all duration-300 ${
                    location.pathname === link.path 
                      ? 'w-6' 
                      : 'w-0 group-hover:w-6'
                  }`}
                ></span>
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 cursor-pointer transition-colors duration-300 ${
              isScrolled ? 'text-navy-900' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`lg:hidden bg-white/98 backdrop-blur-md border-t border-navy-100 transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-6 space-y-1">
          {navLinks.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 ${
                location.pathname === link.path
                  ? 'text-champagne-600 bg-champagne-50'
                  : 'text-navy-700 hover:text-champagne-600 hover:bg-champagne-50/50'
              }`}
              style={{ 
                animationDelay: `${index * 50}ms`,
                animation: isMobileMenuOpen ? 'slideUp 0.4s ease-out forwards' : 'none'
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}
